import React from "react";
import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { Lightbulb, Handshake, TrendingUp } from "lucide-react";

const container = {
  hidden: {},
  show: {
    transition: { staggerChildren: 0.14 },
  },
};

const item = {
  hidden: { opacity: 0, y: 24 },
  show: {
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, ease: [0.16, 1, 0.3, 1] },
  },
};

const approaches = [
  {
    key: "innovation",
    icon: Lightbulb,
    gradient: "from-blue-500 to-indigo-600",
    glow: "bg-blue-400/30",
  },
  {
    key: "partnership",
    icon: Handshake,
    gradient: "from-violet-500 to-fuchsia-600",
    glow: "bg-violet-400/30",
  },
  {
    key: "impact",
    icon: TrendingUp,
    gradient: "from-emerald-500 to-teal-600",
    glow: "bg-emerald-400/30",
  },
];

export const OurApproach = () => {
  const { t } = useTranslation();

  return (
    <section className="relative py-24 sm:py-32 overflow-hidden bg-gray-200">
      {/* Ambient background */}
      <div className="absolute -top-24 right-0 w-[28rem] h-[28rem] bg-white-200/30 blur-3xl rounded-full" />
      <div className="absolute bottom-0 -left-24 w-[24rem] h-[24rem] bg-gray-200/30 blur-3xl rounded-full" />

      <motion.div
        className="relative mx-auto max-w-6xl px-4 sm:px-6 lg:px-12 text-center"
        variants={container}
        initial="hidden"
        whileInView="show"
        viewport={{ once: true }}
      >
        {/* Title */}
        <motion.h2
          variants={item}
          className="text-3xl sm:text-4xl md:text-5xl font-extrabold
                     text-slate-900 mb-6 tracking-tight"
        >
          {t("approach.title")}
        </motion.h2>

        {/* Subtitle */}
        <motion.p
          variants={item}
          className="text-base sm:text-lg text-slate-600
                     leading-relaxed max-w-3xl mx-auto mb-16"
        >
          {t("approach.subtitle")}
        </motion.p>

        {/* Cards */}
        <motion.div
          variants={container}
          className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8"
        >
          {approaches.map(({ key, icon: Icon, gradient, glow }) => (
            <motion.div
              key={key}
              variants={item}
              whileHover={{ y: -10 }}
              className="group relative rounded-3xl bg-white p-8 sm:p-10
                         shadow-md hover:shadow-2xl
                         transition-all duration-300"
            >
              {/* Glow */}
              <div
                className={`absolute -inset-6 rounded-3xl blur-2xl opacity-0
                            group-hover:opacity-100 transition-opacity duration-300 ${glow}`}
              />

              {/* Content */}
              <div className="relative z-10 text-left">
                {/* Icon */}
                <div
                  className={`inline-flex items-center justify-center
                              w-14 h-14 rounded-2xl mb-6
                              bg-gradient-to-br ${gradient}
                              text-white shadow-lg`}
                >
                  <Icon size={26} />
                </div>

                <h3 className="text-xl font-semibold text-slate-900 mb-3">
                  {t(`approach.${key}_title`)}
                </h3>

                <p className="text-sm sm:text-base text-slate-600 leading-relaxed">
                  {t(`approach.${key}_desc`)}
                </p>

                {/* Accent bar */}
                <div
                  className={`mt-8 h-1.5 w-12 rounded-full
                              bg-gradient-to-r ${gradient}
                              transition-all duration-300 group-hover:w-20`}
                />
              </div>
            </motion.div>
          ))}
        </motion.div>
      </motion.div>
    </section>
  );
};
